import { useState } from "react";
import { DataGrid } from '@mui/x-data-grid';
import { Box, TextField, Typography, styled } from "@mui/material";
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../hooks/useModal'
import { Button } from "../Button";
import { setSingleProductInfo } from "../../features/commerce/commerceSlice";
import infoIcon from '../../assets/images/infoIcon.svg';

const StyledDataGrid = styled(DataGrid)(
    ({ theme: { breakpoints } }) =>`
        height: auto;

        ${breakpoints.between('sm', 'md')} {
            &[data-testid="productTable"]{
                height: 600px !important;
            }

            & .MuiDataGrid-row {
                min-height: 155px !important;
                max-height: 155px !important;
        
                & [role="cell"] {
                min-height: 155px !important;
                max-height: 155px !important;
                }
            }
        }

        ${breakpoints.between('md', 'lg2')} {
            &[data-testid="productTable"] {
                height: 510px !important;
            }

            & .MuiDataGrid-row {
                min-height: 145px !important;
                max-height: 145px !important;
        
                & [role="cell"] {
                min-height: 145px !important;
                max-height: 145px !important;
                }
            }
        }
    `);
  
const OrderProductTable = ({ products, isAuthenticated, setOpenModal, handleDosesChange, isReservation=false }) =>{
    const [productObj, setProductObj] = useState({ productId: '', doseQty: 0 });
    const dispatch = useDispatch();
    const { handleOpenModal } = useModal();
    const orderableProducts = useSelector((store) => store?.commerce?.orderableProducts) || [];
    const reservationProducts = useSelector((store) => store?.commerce?.reservationProducts) || [];
    const productDetailList = isReservation?reservationProducts:orderableProducts;

    const TitleComponent = (value) => {
        const row = value.value.row;
        const isSpikevax = row.productId === "70506";
        return (
            <div>
                <Box display="flex" alignItems="center" height={4} mb={2}>
                    <Box pl={2} style={{
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word'
                    }}>
                        <Typography style={{fontSize:'16px', fontWeight:'700', lineHeight:'20px'}}>
                            {row.productName}
                        </Typography>
                        {isSpikevax && (
                            <Box
                                display="flex"
                                alignItems="flex-start"
                                gap={1}
                                pt={1}
                            >
                                <img
                                src={infoIcon}
                                alt="info-icon"
                                width="16"
                                height="16"
                                style={{ marginTop: 2 }}
                                />
                                <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '14px',
                                    color: '#B22222',
                                    fontFamily: 'Aeonik Bold',
                                    lineHeight: 1.4
                                }}
                                >
                                Currently shipping product with July 16th, 2025 expiry.
                                </Typography>
                            </Box>
                            )}
                        {!isReservation &&
                            <Button
                                data-testid={`viewProductDetails-${row.productId}`}
                                sx={{padding: '0', textDecoration: 'none'}}
                                buttonType='link'
                                onClick={() => handleClickProductDetails(row.productId)}
                            >
                                <Typography sx={{textTransform: 'none'}} fontSize="14px" fontFamily="Aeonik Regular">
                                    View product details
                                </Typography>
                            </Button>
                        }
                    </Box>
                </Box>
            </div>
        );
    };

    const handleClickProductDetails = (id) => {
        const singleProductInfo = productDetailList?.find(item => item?.materialMasterId === id);
        dispatch(setSingleProductInfo(singleProductInfo));
        setOpenModal(true);
    }

    const columns = [
        {
            field: 'productName',
            headerName:'Product',
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => (
                <TitleComponent value={params}/>
            )
        },
        {
            field: 'listPrice',
            headerName: isReservation?'List Price*':'List Price',
            headerAlign: 'center',
            align: 'center',
            sortable: false,
        },
        {
            field: 'contractPrice',
            headerName: 'Contract Price',
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            minWidth: 125
        },
        {
            field: 'doses',
            headerName: 'Doses',
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            minWidth: 125,
            renderCell: (params) => {
                let val = params?.value
                if (productObj?.productId === params.id) {
                    val = productObj?.doseQty
                }
                return (
                    <TextField
                        type="number"
                        value={isAuthenticated ? val : 0}
                        onChange={(e) => updateTextVal(params.id, e.target.value)}
                        onBlur={(e) => handleDosesUpdate(params, e)}
                        onKeyDown={(e) => handleDosesUpdate(params, e)}
                        onClick={!isAuthenticated ? handleOpenModal : undefined}
                        inputProps={{min: 0, "data-testid": `doseQty-${params.id}`,'aria-label': 'number of doses'}}
                        size="small"
                        variant="outlined"
                        sx={{
                            '& .MuiInputBase-input': {
                                color: !isAuthenticated && 'text.disabled',
                                cursor: !isAuthenticated && 'default',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: !isAuthenticated && 'text.disabled',
                            },
                        }}
                    />)
            }
        },
    ];
    const filteredColumns = isAuthenticated ? columns : columns.filter(column => column.field === 'productName' || column.field === 'doses');

    const handleDosesUpdate = (product, event) => {
        const { id, value } = product
        const dose = Number(event.target.value);
        if (value === dose) return;
        if (event?.key === 'Enter' || event?.type === 'blur') {
            const doseQty = (Math.ceil(dose / 10)) * 10;
            setProductObj((prevState) =>({ ...prevState, productId: id, doseQty}));
            handleDosesChange(id, dose)
        }
    };

    const updateTextVal = (productId, doseQty) => {
        if (doseQty.charAt(0) === '0') {
            doseQty = doseQty.slice(1);
        }
        setProductObj((prevState) =>({...prevState, productId, doseQty: doseQty}));
    };

    return (
        <StyledDataGrid
            data-testid={"productTable"}
            autoHeight
            rowHeight={90}
            rows={products || []}
            columns={filteredColumns || []}
            hideFooter
            disableColumnFilter
            disableColumnMenu
        />
    );
}

export default OrderProductTable;
